import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "hg_install_dismiss_until";
const DISMISS_HOURS = 72;

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator?.standalone === true
    );
}

function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
}

function isDismissed() {
    try {
        const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        return Date.now() < until;
    } catch {
        return false;
    }
}

function dismiss() {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_HOURS * 3600 * 1000));
    } catch {}
}

export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [showIos, setShowIos] = useState(false);
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (isStandalone() || isDismissed()) return;

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShow(true);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        // iOS Safari has no event — show a one-time hint after a short delay.
        if (isIOS()) {
            const t = setTimeout(() => {
                if (!isStandalone() && !isDismissed()) {
                    setShowIos(true);
                    setShow(true);
                }
            }, 4000);
            return () => {
                clearTimeout(t);
                window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            };
        }

        return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    }, []);

    if (!show) return null;

    const handleInstall = async () => {
        if (!deferred) return;
        deferred.prompt();
        try {
            await deferred.userChoice;
        } catch {}
        setDeferred(null);
        setShow(false);
        dismiss();
    };

    const handleClose = () => {
        dismiss();
        setShow(false);
    };

    return (
        <div
            data-testid="install-prompt"
            className="fixed bottom-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-xl border border-white/10 bg-zinc-950/95 p-4 shadow-2xl backdrop-blur sm:bottom-6"
        >
            <button
                onClick={handleClose}
                aria-label="Dismiss install prompt"
                data-testid="install-dismiss"
                className="absolute right-2 top-2 rounded p-1 text-white/60 hover:bg-white/5 hover:text-white"
            >
                <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FF3B30]">
                    <Download className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                    <p className="text-sm font-semibold text-white">Install Hypergains</p>
                    {showIos ? (
                        <p className="mt-1 text-xs leading-snug text-white/70">
                            Tap{" "}
                            <Share className="mx-0.5 inline h-3.5 w-3.5 align-text-bottom" />{" "}
                            <span className="text-white">Share</span> then{" "}
                            <span className="text-white">Add to Home Screen</span> for one-tap access.
                        </p>
                    ) : (
                        <p className="mt-1 text-xs leading-snug text-white/70">
                            One-tap workouts. No tab juggling. Save to your home screen.
                        </p>
                    )}
                    {!showIos && (
                        <button
                            onClick={handleInstall}
                            data-testid="install-accept"
                            className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#FF3B30] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#ff5147]"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Install app
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
